import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Download, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { api, unwrap } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import { PermissionGate } from '@/auth/PermissionGate';
import { useCan } from '@/auth/queries';
import { useStepUp } from '@/auth/StepUp';
import { Cargando, Encabezado, ErrorCarga, Vacio } from '@/components/Estados';
import { Paginador } from '@/components/Paginador';
import { useFiltrosUrl } from '@/hooks/useFiltrosUrl';
import { guardarArchivo } from '@/lib/descargar';
import { fechaHora } from '@/lib/formato';

import { useDirectorio, useOpcionesLicencia, type FiltrosDirectorio } from './queries';

const CLAVES = ['q', 'status', 'kind', 'membershipId', 'from', 'to', 'sort'] as const;

/** Activa una por una (cada cambio queda en auditoría). Devuelve las que fallaron para reintentar. */
async function activarAlumnos(ids: number[]) {
  const fallidos: number[] = [];
  let error: unknown = null;
  for (const id of ids) {
    try {
      await unwrap(
        api.PATCH('/api/v1/admin/users/{id}', {
          params: { path: { id: String(id) } },
          body: { isActive: true },
        }),
      );
    } catch (e) {
      fallidos.push(id);
      error ??= e;
    }
  }
  return { activadas: ids.length - fallidos.length, fallidos, error };
}

function Directorio() {
  const { filtros, cambiar: cambiarUrl, query } = useFiltrosUrl(CLAVES);
  const puedeEditar = useCan('alumnos', 'editar');
  const opciones = useOpcionesLicencia();
  const { proteger, dialogo } = useStepUp();
  const qc = useQueryClient();
  const [exportando, setExportando] = useState(false);
  const [seleccion, setSeleccion] = useState<Set<number>>(() => new Set());
  const consulta = { ...query, page: filtros.page, pageSize: 25 } as FiltrosDirectorio;
  const q = useDirectorio(consulta);

  // Al cambiar filtros, orden o página la selección deja de corresponder a la lista visible.
  const cambiar = (cambios: Parameters<typeof cambiarUrl>[0]) => {
    setSeleccion(new Set());
    cambiarUrl(cambios);
  };

  const activar = useMutation({
    mutationFn: (ids: number[]) => activarAlumnos(ids),
    onSuccess: ({ activadas, fallidos, error }) => {
      if (activadas > 0) {
        toast.success(activadas === 1 ? '1 alumno activado' : `${activadas} alumnos activados`);
      }
      if (fallidos.length > 0) {
        toast.error(`No se pudieron activar ${fallidos.length}: ${mensajeDe(error)}`);
      }
      setSeleccion(new Set(fallidos));
      void qc.invalidateQueries({ queryKey: ['alumnos'] });
    },
  });

  // Solo se seleccionan cuentas inactivas: activar una cuenta ya activa no hace nada.
  const inactivas = q.data?.data.filter((a) => !a.isActive) ?? [];
  const todasEnPagina = inactivas.length > 0 && inactivas.every((a) => seleccion.has(a.id));
  const alternar = (id: number) =>
    setSeleccion((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const alternarPagina = () =>
    setSeleccion((prev) => {
      const next = new Set(prev);
      inactivas.forEach((a) => (todasEnPagina ? next.delete(a.id) : next.add(a.id)));
      return next;
    });

  const exportar = async () => {
    setExportando(true);
    try {
      await proteger(async () =>
        guardarArchivo(
          await api.GET('/api/v1/admin/users/export', {
            params: { query },
            parseAs: 'blob',
          }),
          'alumnos.xlsx',
        ),
      );
    } catch (e) {
      toast.error(mensajeDe(e));
    } finally {
      setExportando(false);
    }
  };

  return (
    <>
      <Encabezado
        titulo="Alumnos"
        descripcion="Busca por ID, nombre, correo o teléfono."
        acciones={
          <>
            <button
              type="button"
              className="btn-cs btn-contorno"
              onClick={() => void exportar()}
              disabled={exportando}
            >
              <Download size={16} aria-hidden /> {exportando ? 'Generando…' : 'Exportar .xlsx'}
            </button>
            {puedeEditar && (
              <Link to="/alumnos/nuevo" className="btn-cs btn-primario">
                <Plus size={16} aria-hidden /> Nuevo alumno
              </Link>
            )}
          </>
        }
      />
      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
        <input
          className="input-vidrio xl:col-span-2"
          placeholder="ID, nombre, correo o teléfono"
          defaultValue={filtros.q}
          onKeyDown={(e) => e.key === 'Enter' && cambiar({ q: e.currentTarget.value.trim() })}
          onBlur={(e) => cambiar({ q: e.target.value.trim() })}
          aria-label="Buscar"
        />
        <select
          className="input-vidrio"
          value={filtros.status}
          onChange={(e) => cambiar({ status: e.target.value })}
          aria-label="Estado de la cuenta"
        >
          <option value="">Cualquier estado</option>
          <option value="active">Activas</option>
          <option value="inactive">Inactivas</option>
          <option value="suspended">Suspendidas</option>
          <option value="blocked">Bloqueadas</option>
        </select>
        <select
          className="input-vidrio"
          value={filtros.kind}
          onChange={(e) => cambiar({ kind: e.target.value })}
          aria-label="Tipo de alumno"
        >
          <option value="">Pagados y demo</option>
          <option value="current">Con licencia vigente</option>
          <option value="paid">Con algún pago</option>
          <option value="demo">Sin pagos (demo)</option>
        </select>
        <select
          className="input-vidrio"
          value={filtros.membershipId}
          onChange={(e) => cambiar({ membershipId: e.target.value })}
          aria-label="Con licencia vigente"
        >
          <option value="">Cualquier licencia</option>
          {opciones.data?.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
              {o.isActive ? '' : ' (inactiva)'}
            </option>
          ))}
        </select>
        <input
          type="date"
          className="input-vidrio"
          value={filtros.from}
          onChange={(e) => cambiar({ from: e.target.value })}
          aria-label="Registrados desde"
          title="Registrados desde"
        />
        <input
          type="date"
          className="input-vidrio"
          value={filtros.to}
          onChange={(e) => cambiar({ to: e.target.value })}
          aria-label="Registrados hasta"
          title="Registrados hasta"
        />
      </div>
      {q.isPending && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {q.data?.data.length === 0 && <Vacio titulo="Sin alumnos con esos filtros" />}
      {puedeEditar && seleccion.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-3 text-sm">
          <span className="font-semibold">{seleccion.size} seleccionadas</span>
          <button
            type="button"
            className="btn-cs btn-primario"
            disabled={activar.isPending}
            onClick={() => activar.mutate([...seleccion])}
          >
            {activar.isPending ? 'Activando…' : 'Activar seleccionadas'}
          </button>
          <button
            type="button"
            className="btn-cs btn-contorno"
            disabled={activar.isPending}
            onClick={() => setSeleccion(new Set())}
          >
            Quitar selección
          </button>
        </div>
      )}
      {q.data && q.data.data.length > 0 && (
        <div className="panel-vidrio overflow-x-auto p-0">
          <table className="tabla-cs w-full text-sm">
            <thead>
              <tr>
                {puedeEditar && (
                  <th className="w-10">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-[var(--color-azul-600)]"
                      aria-label="Seleccionar las inactivas de esta página"
                      checked={todasEnPagina}
                      disabled={inactivas.length === 0 || activar.isPending}
                      onChange={alternarPagina}
                    />
                  </th>
                )}
                <th>
                  <button
                    type="button"
                    onClick={() => cambiar({ sort: filtros.sort === '-id' ? 'id' : '-id' })}
                  >
                    ID
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    onClick={() => cambiar({ sort: filtros.sort === 'name' ? '-name' : 'name' })}
                  >
                    Nombre
                  </button>
                </th>
                <th>Correo</th>
                <th>Licencias vigentes</th>
                <th>Estado</th>
                <th>
                  <button
                    type="button"
                    onClick={() =>
                      cambiar({
                        sort: filtros.sort === '-lastLoginAt' ? 'lastLoginAt' : '-lastLoginAt',
                      })
                    }
                  >
                    Último acceso
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {q.data.data.map((a) => (
                <tr key={a.id}>
                  {puedeEditar && (
                    <td>
                      {!a.isActive && (
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-[var(--color-azul-600)]"
                          aria-label={`Seleccionar a ${a.name}`}
                          checked={seleccion.has(a.id)}
                          disabled={activar.isPending}
                          onChange={() => alternar(a.id)}
                        />
                      )}
                    </td>
                  )}
                  <td className="tabular">{a.id}</td>
                  <td>
                    <Link to={`/alumnos/${a.id}`} className="font-bold">
                      {a.name}
                    </Link>
                  </td>
                  <td>{a.email}</td>
                  <td>
                    {a.activeMemberships.map((m) => m.name).join(', ') || (
                      <span className="text-texto-3">—</span>
                    )}
                  </td>
                  <td className="space-x-1 whitespace-nowrap">
                    {!a.isActive && <span className="insignia">Inactiva</span>}
                    {a.suspended && <span className="insignia insignia-exclusivo">Suspendida</span>}
                    {a.blocked && <span className="insignia insignia-exclusivo">Bloqueada</span>}
                    {a.isActive && !a.suspended && !a.blocked && (
                      <span className="insignia insignia-activa">Activa</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap">{fechaHora(a.lastLoginAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {q.data && (
        <Paginador
          page={q.data.page}
          pageSize={q.data.pageSize}
          total={q.data.total}
          onPage={(page) => cambiar({ page })}
        />
      )}
      {dialogo}
    </>
  );
}

export function DirectorioPage() {
  return (
    <PermissionGate seccion="alumnos">
      <Directorio />
    </PermissionGate>
  );
}
